import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

const LegalAcceptanceModal: React.FC = () => {
  const { me, isLoading, refresh } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [termsOk, setTermsOk] = useState(false);
  const [privacyOk, setPrivacyOk] = useState(false);
  const [cookiesOk, setCookiesOk] = useState(false);
  const [gdprOk, setGdprOk] = useState(false);

  const needs = useMemo(() => {
    if (!me) return { show: false, needsTerms: false, needsPrivacy: false, needsCookies: false, needsGdpr: false };
    const needsTerms = !me.terms_accepted;
    const needsPrivacy = !me.privacy_accepted;
    const needsCookies = !me.cookies_accepted;
    const needsGdpr = !me.gdpr_accepted;
    return {
      show: needsTerms || needsPrivacy || needsCookies || needsGdpr,
      needsTerms,
      needsPrivacy,
      needsCookies,
      needsGdpr,
    };
  }, [me]);

  if (isLoading || !me || !needs.show) return null;

  const canSubmit =
    (!needs.needsTerms || termsOk) &&
    (!needs.needsPrivacy || privacyOk) &&
    (!needs.needsCookies || cookiesOk) &&
    (!needs.needsGdpr || gdprOk);

  return (
    <div className="fixed inset-0 z-[1200] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-lg rounded-lg border border-border bg-card text-foreground shadow-xl">
        <div className="p-5 border-b border-border">
          <h2 className="text-lg font-semibold">Acorduri necesare</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Pentru a continua, te rugăm să confirmi acordul pentru documentele legale.
          </p>
        </div>

        <div className="p-5 space-y-4">
          {needs.needsTerms && (
            <label className="flex items-start gap-3 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={termsOk}
                onChange={(e) => setTermsOk(e.target.checked)}
              />
              <span>
                Am citit și accept{' '}
                <Link className="underline" to="/legal/termeni-si-conditii" target="_blank">
                  Termenii și condițiile
                </Link>
                .
              </span>
            </label>
          )}

          {needs.needsPrivacy && (
            <label className="flex items-start gap-3 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={privacyOk}
                onChange={(e) => setPrivacyOk(e.target.checked)}
              />
              <span>
                Am citit și accept{' '}
                <Link className="underline" to="/legal/politica-de-confidentialitate" target="_blank">
                  Politica de confidențialitate
                </Link>
                .
              </span>
            </label>
          )}

          {needs.needsCookies && (
            <label className="flex items-start gap-3 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={cookiesOk}
                onChange={(e) => setCookiesOk(e.target.checked)}
              />
              <span>
                Am luat la cunoștință{' '}
                <Link className="underline" to="/legal/politica-de-cookies" target="_blank">
                  Politica de cookies
                </Link>
                .
              </span>
            </label>
          )}

          {needs.needsGdpr && (
            <label className="flex items-start gap-3 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={gdprOk}
                onChange={(e) => setGdprOk(e.target.checked)}
              />
              <span>
                Sunt de acord cu prelucrarea datelor conform{' '}
                <Link className="underline" to="/legal/gdpr" target="_blank">
                  GDPR
                </Link>
                .
              </span>
            </label>
          )}

          {!needs.needsCookies ? (
            <div className="text-xs text-muted-foreground">
              Poți consulta și{' '}
              <Link className="underline" to="/legal/politica-de-cookies" target="_blank">
                Politica de cookies
              </Link>
              .
            </div>
          ) : null}
        </div>

        <div className="p-5 border-t border-border flex items-center justify-end gap-2">
          <button
            type="button"
            className="px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-50"
            disabled={!canSubmit || isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                if (needs.needsTerms || needs.needsPrivacy || needs.needsCookies) {
                  const documents: string[] = [];
                  if (needs.needsTerms) documents.push('terms');
                  if (needs.needsPrivacy) documents.push('privacy');
                  if (needs.needsCookies) documents.push('cookies');
                  await api.post('/api/legal/accept', { documents });
                }
                if (needs.needsGdpr) {
                  await api.post('/api/gdpr/accept', {});
                }
                await refresh();
              } catch (e: any) {
                const msg = e?.response?.data?.detail || e?.message || 'Eroare la salvarea acordurilor.';
                window.alert(msg);
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? 'Se salvează...' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LegalAcceptanceModal;
